import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { DocumentBinary} from '../../interfaces/documents/document-binary';
import { RegHubApiResult } from '../../interfaces/reghubapi-result';
import { SearchResult } from '../../interfaces/search-results/search-result';
import { SearchResultForUpdate } from '../../interfaces/search-results/search-result-for-update';
import { SearchResultForCreation } from '../../interfaces/search-results/search-result-for-creation';
import { REG_HUB_COMMON_CONFIG, RegHubCommonConfig } from '../../reg-hub-common-config';
import { Observable } from 'rxjs';
import { SearchSummary } from '../../interfaces/search-summaries/search-summary';
import { ParsedBusinessReportEntity } from '../../interfaces/business-report-parsed-results/parsed-business-report-entity';
import { ParsedResultForUpdate } from '../../interfaces/parsed-result-for-update';
import { InternalHttpOptions } from '../../interfaces/internal-http-options';

@Injectable({
    providedIn: 'root'
})
export class SearchResultsRepositoryService {
    protected _baseLink: string;

    constructor(
        private http: HttpClient,
        @Inject(REG_HUB_COMMON_CONFIG) private config: RegHubCommonConfig) {
        this._baseLink = `${config.rootUrl}/Orders`;
    }

    private generateHeaders = () => {
        return {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' })
        }
    }

    public getSearchResult = (orderID: string) => {
        return this.http.get<SearchResult>(`${this._baseLink}/${orderID}/SearchResults`, this.generateHeaders());
    }

    public getSearchResultDocument = (orderID: string) => {
        return this.http.get<DocumentBinary>(`${this._baseLink}/${orderID}/SearchResults/Document`, this.generateHeaders());
    }

    public getSearchResultSummaryDocument = (orderID: string) => {
        return this.http.get<DocumentBinary>(`${this._baseLink}/${orderID}/SearchResults/Summary`, this.generateHeaders());
    }
    
    public getSearchSummaryAsJson(orderID: string, options?: InternalHttpOptions): Observable<SearchSummary> {
        const headers = new HttpHeaders()
            .append('accept', 'application/json');
        let params = new HttpParams();
        
        options?.hideErrorsStatusCodes?.forEach(code => params = params.append('hideErrorsStatusCodes', code));

        return this.http.get<SearchSummary>(`${this._baseLink}/${orderID}/Results/Summary`, { headers: headers, params: params });
    }

    public getSearchResultRawDocument = (orderID: string) => {
        return this.http.get<DocumentBinary>(`${this._baseLink}/${orderID}/SearchResults/Raw`, this.generateHeaders());
    }

    public createSearchResult = (orderID: string, searchResult: SearchResultForCreation) => {
        return this.http.post<RegHubApiResult>(`${this._baseLink}/${orderID}/SearchResults`, searchResult, this.generateHeaders());
    }

    public updateSearchResult = (orderID: string, searchResult: SearchResultForUpdate) => {
        return this.http.put<RegHubApiResult>(`${this._baseLink}/${orderID}/SearchResults`, searchResult, this.generateHeaders());
    }

    public getBusinessReportParsedResults(orderID: string): Observable<ParsedBusinessReportEntity> {
        const headers = new HttpHeaders()
            .append('accept', 'application/json');
        
        return this.http.get<ParsedBusinessReportEntity>(`${this._baseLink}/${orderID}/Results/Document`, { headers: headers });
    }

    public getBusinessReportParsedResultsAsJson(orderID: string): Observable<JSON> {
        const headers = new HttpHeaders()
            .append('accept', 'application/json');
        
        return this.http.get<JSON>(`${this._baseLink}/${orderID}/Results/Document`, { headers: headers });
    }

    public getParsedResults(orderID: string): Observable<JSON> {
        const headers = new HttpHeaders()
            .append('accept', 'application/json');

        return this.http.get<JSON>(`${this._baseLink}/${orderID}/ParsedResults`, { headers: headers });
    }

    public updateParsedResults(orderID: string, parsedResultsForUpdate: ParsedResultForUpdate): Observable<RegHubApiResult> {
        return this.http.put<RegHubApiResult>(`${this._baseLink}/${orderID}/ParsedResults`, parsedResultsForUpdate, this.generateHeaders());
    }
}